import { FC, useEffect, useState } from "react";
import {
  Dialog,
  IconButton,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
} from "@mui/material";
import User from "../../../../../Models/UserModels/User";
import {
  getCatchCompareReport,
  getReportPDF,
} from "../../../../../Services/LondunarkerfiAPIService";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import CatchCompare from "../../../../../Models/ReportModels/CatchCompare";
import {
  MobilePaperComponent,
  PaperComponent,
} from "../../../../../SharedComponents/Paper/CustomPaper";
import { logError } from "../../../../../Helpers/LogError";
import CancelIcon from "@mui/icons-material/Cancel";
import SendReportModal from "../../../../Reports/components/SendReportModal";

interface CompareCatchModalProps {
  compareCatchModalOpen: boolean;
  toggleCompareCatchModalOpen: () => void;
  user: User;
  traceId: number;
  showSnackbar: (message: string, severity: string) => void;
}

const CompareCatchModal: FC<CompareCatchModalProps> = (props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [traceData, setTraceData] = useState<CatchCompare[]>([]);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    setLoading(true);
    const getTraceIds = async () => {
      try {
        if (props.traceId && props.compareCatchModalOpen) {
          const traceData = await getCatchCompareReport(props.traceId);
          if (traceData) {
            setTraceData(traceData);
          }
        }
        setLoading(false);
      } catch (error) {
        logError(error);
        setLoading(false);
      }
    };
    getTraceIds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.compareCatchModalOpen, props.traceId]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getRowClassName = (params: any) => {
    return params.row?.registeredCatch === 0 && params.row?.declaredCatch > 0
      ? "missing"
      : params.row?.registeredCatch > 0 && params.row?.declaredCatch === 0
        ? "not-in-declared"
        : "";
  };

  const columns: GridColDef[] = [
    {
      field: "fishingStock",
      headerName: "Veiðistofn",
      valueGetter: (params) => params.row.fishingStock.name,
      flex: 1,
    },
    { field: "registeredCatch", headerName: "Skráð magn" },
    { field: "declaredCatch", headerName: "Magn afladagbók", flex: 1 },
    { field: "difference", headerName: "Mismunur" },
  ];

  const StyledDataGrid = styled(DataGrid)`
    .missing {
      background-color: #e57373;
    }
    .not-in-declared {
      background-color: #ffb74d;
    }
  `;

  const handleClose = () => {
    props.toggleCompareCatchModalOpen();
    setTraceData([]);
  };

  const formatDate = (date: Date) => {
    const d = date.getDate().toString().padStart(2, "0");
    const m = (date.getMonth() + 1).toString().padStart(2, "0");
    const y = date.getFullYear();
    return `${d}.${m}.${y}`;
  };

  // PDF Export Handler
  const handleDownloadPDF = async () => {
    try {
      setPdfLoading(true);
      const pdfBlob = await getReportPDF(
        "catchCompare",
        props.user.userPickedHarbour,
        undefined,
        undefined,
        undefined,
        undefined,
        props.traceId
      );
      if (pdfBlob instanceof Blob) {
        const saveAs = (await import("file-saver")).default;
        saveAs(pdfBlob, "SamanburðurAfla.pdf");
      }
    } catch (error) {
      logError(error);
      // Optionally show snackbar
    } finally {
      setPdfLoading(false);
    }
  };

  // Send Report Handler (open modal or call API)
  const handleSendReport = () => {
    setSendModalOpen(true);
  };

  const handleSendModalClose = () => setSendModalOpen(false);

  return (
    <div>
      <Dialog
        open={props.compareCatchModalOpen}
        onClose={!loading ? handleClose : undefined}
        disableEscapeKeyDown={loading}
        PaperComponent={
          isMobile || isTablet ? MobilePaperComponent : PaperComponent
        }
        aria-labelledby="draggable-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
          }}
        >
          <Typography
            sx={{ fontWeight: "bold", fontSize: "1.5em", cursor: "move" }}
          >
            Samanburður á afla
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <button
              onClick={handleSendReport}
              style={{
                background: loading
                  ? theme.palette.mode === "dark"
                    ? "linear-gradient(90deg, #374151 0%, #23272f 100%)"
                    : "linear-gradient(90deg, #cbd5e1 0%, #94a3b8 100%)"
                  : theme.palette.mode === "dark"
                    ? "linear-gradient(90deg, #2563eb 0%, #0ea5e9 100%)"
                    : "linear-gradient(90deg, #3b82f6 0%, #06b6d4 100%)",
                color: loading
                  ? theme.palette.mode === "dark"
                    ? "#94a3b8"
                    : "#64748b"
                  : "#fff",
                border: "none",
                borderRadius: 8,
                padding: "10px 24px",
                fontSize: 16,
                fontWeight: 600,
                cursor: loading ? "not-allowed" : "pointer",
                boxShadow: loading
                  ? "none"
                  : theme.palette.mode === "dark"
                    ? "0 2px 8px rgba(59,130,246,0.18)"
                    : "0 2px 8px rgba(59,130,246,0.08)",
                transition: "background 0.2s",
                display: "flex",
                alignItems: "center",
                gap: 8,
                opacity: loading ? 0.6 : 1,
                minWidth: 80,
                maxWidth: "100%",
                boxSizing: "border-box",
              }}
              disabled={loading}
            >
              <span role="img" aria-label="send">
                📤
              </span>
              Senda
            </button>
            <button
              onClick={handleDownloadPDF}
              style={{
                background: pdfLoading
                  ? theme.palette.mode === "dark"
                    ? "linear-gradient(90deg, #374151 0%, #23272f 100%)"
                    : "linear-gradient(90deg, #cbd5e1 0%, #94a3b8 100%)"
                  : theme.palette.mode === "dark"
                    ? "linear-gradient(90deg, #ef4444 0%, #2563eb 100%)"
                    : "linear-gradient(90deg, #ef4444 0%, #3b82f6 100%)",
                color: pdfLoading
                  ? theme.palette.mode === "dark"
                    ? "#94a3b8"
                    : "#64748b"
                  : "#fff",
                border: "none",
                borderRadius: 8,
                padding: "10px 24px",
                fontSize: 16,
                fontWeight: 600,
                cursor: pdfLoading || loading ? "not-allowed" : "pointer",
                boxShadow: pdfLoading
                  ? "none"
                  : theme.palette.mode === "dark"
                    ? "0 2px 8px rgba(239,68,68,0.18)"
                    : "0 2px 8px rgba(239,68,68,0.08)",
                transition: "background 0.2s",
                display: "flex",
                alignItems: "center",
                gap: 8,
                opacity: pdfLoading || loading ? 0.6 : 1,
                minWidth: 80,
                maxWidth: "100%",
                boxSizing: "border-box",
                position: "relative",
              }}
              disabled={pdfLoading || loading}
            >
              <span role="img" aria-label="download-pdf">
                📄
              </span>
              {pdfLoading ? (
                <span
                  style={{
                    marginLeft: 8,
                    fontSize: 16,
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      border: "2px solid #fff",
                      borderTop: "2px solid #ef4444",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                      marginRight: 8,
                      display: "inline-block",
                    }}
                  />
                  Sæki PDF...
                </span>
              ) : (
                "PDF"
              )}
            </button>
            <IconButton
              onClick={props.toggleCompareCatchModalOpen}
              sx={{
                background:
                  theme.palette.mode === "dark"
                    ? "linear-gradient(90deg, #374151 0%, #23272f 100%)"
                    : "linear-gradient(90deg, #cbd5e1 0%, #94a3b8 100%)",
                borderRadius: 8,
                ml: 1,
                color: theme.palette.mode === "dark" ? "#94a3b8" : "#64748b",
                boxShadow: "none",
                height: 44,
                width: 44,
              }}
              disabled={loading}
            >
              <CancelIcon fontSize="large" />
            </IconButton>
            <style>
              {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              `}
            </style>
          </Box>
        </Box>
        <StyledDataGrid
          rows={traceData}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.fishingStock.id}
          getRowClassName={getRowClassName}
          autoHeight
        />
        {sendModalOpen && (
          <SendReportModal
            open={sendModalOpen}
            toggleOpen={handleSendModalClose}
            user={props.user}
            selectedReport={{
              id: "catchCompare",
              title: "Samanburður á afla",
              reportDate: formatDate(new Date()),
              description: "Samanburður á skráðum og tilkynntum afla.",
            }}
            showSnackbar={props.showSnackbar}
            isMobileView={false}
            filterModel={{ logic: "and", clauses: [] }}
            groupBy={undefined}
            dateFrom={""}
            dateTo={""}
            traceId={props.traceId}
          />
        )}
      </Dialog>
    </div>
  );
};

export default CompareCatchModal;
